import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Edit2, Euro, Check, X, Receipt, AlertCircle,
  Loader2, ChevronDown, ChevronUp, Calendar, FileText, Banknote,
  PiggyBank, Calculator, TrendingUp, CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from './ui/collapsible';
import { Progress } from './ui/progress';
import { pagosAPI } from '../api';
import { toast } from '../hooks/use-toast';

const TIPOS_CONCEPTO = {
  principal: { label: 'Principal de la condena/reclamación', icon: '💰', color: 'bg-blue-100 text-blue-700' },
  intereses: { label: 'Intereses legales y moratorios', icon: '📈', color: 'bg-purple-100 text-purple-700' },
  costas_primera: { label: 'Costas de primera instancia', icon: '⚖️', color: 'bg-green-100 text-green-700' },
  costas_segunda: { label: 'Costas de segunda instancia', icon: '⚖️', color: 'bg-teal-100 text-teal-700' },
  costas_tercera: { label: 'Costas de tercera instancia', icon: '⚖️', color: 'bg-cyan-100 text-cyan-700' },
  costas_impugnacion: { label: 'Costas incidente impugnacion de costas', icon: '⚖️', color: 'bg-indigo-100 text-indigo-700' },
  costas_ejecucion: { label: 'Costas de ejecucion', icon: '⚖️', color: 'bg-rose-100 text-rose-700' },
  costas_oposicion: { label: 'Costas de incidente de oposicion a ejecucion', icon: '⚖️', color: 'bg-amber-100 text-amber-700' },
  otras_costas: { label: 'Otras costas', icon: '📋', color: 'bg-orange-100 text-orange-700' },
  otros: { label: 'Otras cantidades', icon: '📝', color: 'bg-gray-100 text-gray-700' },
};

const PagosSection = ({ expedienteId }) => {
  const [loading, setLoading] = useState(true);
  const [conceptos, setConceptos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [tiposDisponibles, setTiposDisponibles] = useState([]);
  const [expandedConceptos, setExpandedConceptos] = useState({});
  
  // Dialogs
  const [isConceptoDialogOpen, setIsConceptoDialogOpen] = useState(false);
  const [isPagoDialogOpen, setIsPagoDialogOpen] = useState(false);
  const [isDeleteConceptoDialogOpen, setIsDeleteConceptoDialogOpen] = useState(false);
  const [isDeletePagoDialogOpen, setIsDeletePagoDialogOpen] = useState(false);
  
  // Forms
  const [editingConcepto, setEditingConcepto] = useState(null);
  const [selectedConcepto, setSelectedConcepto] = useState(null);
  const [selectedPago, setSelectedPago] = useState(null);
  const [conceptoForm, setConceptoForm] = useState({
    tipo: '',
    descripcion: '',
    importe_total: ''
  });
  const [pagoForm, setPagoForm] = useState({
    importe: '',
    fecha_pago: new Date().toISOString().split('T')[0],
    referencia: '',
    notas: ''
  });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [conceptosRes, resumenRes, tiposRes] = await Promise.all([
        pagosAPI.getConceptos(expedienteId),
        pagosAPI.getResumen(expedienteId),
        pagosAPI.getTiposDisponibles(expedienteId)
      ]);
      setConceptos(conceptosRes.data || []);
      setResumen(resumenRes.data);
      setTiposDisponibles(tiposRes.data || []);
    } catch (error) {
      console.error('Error cargando pagos:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos de pagos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [expedienteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleConcepto = (conceptoId) => {
    setExpandedConceptos(prev => ({
      ...prev,
      [conceptoId]: !prev[conceptoId]
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // =============== CONCEPTO HANDLERS ===============
  
  const handleOpenConceptoDialog = (concepto = null) => {
    if (concepto) {
      setEditingConcepto(concepto);
      setConceptoForm({
        tipo: concepto.tipo,
        descripcion: concepto.descripcion || '',
        importe_total: concepto.importe_total.toString()
      });
    } else {
      setEditingConcepto(null);
      setConceptoForm({
        tipo: tiposDisponibles.length > 0 ? tiposDisponibles[0].value : '',
        descripcion: '',
        importe_total: ''
      });
    }
    setIsConceptoDialogOpen(true);
  };

  const handleSaveConcepto = async () => {
    if (!conceptoForm.tipo || !conceptoForm.importe_total) {
      toast({
        title: "Error",
        description: "Selecciona un tipo y especifica el importe",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      if (editingConcepto) {
        await pagosAPI.updateConcepto(expedienteId, editingConcepto.id, {
          descripcion: conceptoForm.descripcion,
          importe_total: parseFloat(conceptoForm.importe_total)
        });
        toast({ title: "Concepto actualizado" });
      } else {
        await pagosAPI.createConcepto(expedienteId, {
          tipo: conceptoForm.tipo,
          descripcion: conceptoForm.descripcion,
          importe_total: parseFloat(conceptoForm.importe_total)
        });
        toast({ title: "Concepto añadido" });
      }
      setIsConceptoDialogOpen(false);
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "No se pudo guardar el concepto",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConcepto = async () => {
    if (!selectedConcepto) return;
    try {
      await pagosAPI.deleteConcepto(expedienteId, selectedConcepto.id);
      toast({ title: "Concepto eliminado" });
      setIsDeleteConceptoDialogOpen(false);
      setSelectedConcepto(null);
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el concepto",
        variant: "destructive"
      });
    }
  };

  // =============== PAGO HANDLERS ===============

  const handleOpenPagoDialog = (concepto) => {
    setSelectedConcepto(concepto);
    setPagoForm({
      importe: '',
      fecha_pago: new Date().toISOString().split('T')[0],
      referencia: '',
      notas: ''
    });
    setIsPagoDialogOpen(true);
  };

  const handleSavePago = async () => {
    if (!pagoForm.importe || !selectedConcepto) {
      toast({
        title: "Error",
        description: "Especifica el importe del pago",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      await pagosAPI.createPago(expedienteId, selectedConcepto.id, {
        importe: parseFloat(pagoForm.importe),
        fecha_pago: pagoForm.fecha_pago ? new Date(pagoForm.fecha_pago).toISOString() : null,
        referencia: pagoForm.referencia,
        notas: pagoForm.notas
      });
      toast({ title: "Pago registrado" });
      setIsPagoDialogOpen(false);
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo registrar el pago",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePago = async () => {
    if (!selectedPago) return;
    try {
      await pagosAPI.deletePago(expedienteId, selectedPago.id);
      toast({ title: "Pago eliminado" });
      setIsDeletePagoDialogOpen(false);
      setSelectedPago(null);
      await loadData();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el pago",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Cargando pagos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Resumen de Pagos */}
      {resumen && (resumen.total_a_cobrar > 0 || conceptos.length > 0) && (
        <Card className={resumen.tiene_pagos_pendientes ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Resumen de Pagos
              </h3>
              {resumen.tiene_pagos_pendientes ? (
                <Badge className="bg-orange-100 text-orange-700">Pagos Pendientes</Badge>
              ) : (
                <Badge className="bg-green-100 text-green-700">Todo Pagado</Badge>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg p-3 border">
                <p className="text-xs text-gray-500">Total a Cobrar</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(resumen.total_a_cobrar)}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border">
                <p className="text-xs text-gray-500">Total Pagado</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(resumen.total_pagado)}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border">
                <p className="text-xs text-gray-500">Pendiente</p>
                <p className="text-xl font-bold text-orange-600">{formatCurrency(resumen.total_pendiente)}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border">
                <p className="text-xs text-gray-500">Progreso</p>
                <div className="flex items-center gap-2">
                  <Progress value={resumen.porcentaje_total_pagado} className="h-2 flex-1" />
                  <span className="text-sm font-medium">{resumen.porcentaje_total_pagado}%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Botón para añadir concepto */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-900">Conceptos de Pago</h3>
        {tiposDisponibles.length > 0 && (
          <Button size="sm" onClick={() => handleOpenConceptoDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Añadir Concepto
          </Button>
        )}
      </div>

      {/* Lista de conceptos */}
      {conceptos.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
          <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h4 className="font-medium text-gray-700 mb-2">No hay conceptos de pago configurados</h4>
          <p className="text-sm text-gray-500 mb-4">Añade los conceptos que se deben cobrar en este expediente</p>
          <Button onClick={() => handleOpenConceptoDialog()}>
            <Plus className="w-4 h-4 mr-2" />
            Añadir Primer Concepto
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {conceptos.map((concepto) => (
            <Collapsible
              key={concepto.id}
              open={expandedConceptos[concepto.id]}
              onOpenChange={() => toggleConcepto(concepto.id)}
            >
              <Card className="overflow-hidden">
                <CollapsibleTrigger asChild>
                  <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{TIPOS_CONCEPTO[concepto.tipo]?.icon || '💰'}</span>
                        <div>
                          <p className="font-medium">{concepto.tipo_display}</p>
                          {concepto.descripcion && (
                            <p className="text-sm text-gray-500">{concepto.descripcion}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            {formatCurrency(concepto.importe_pagado)} / {formatCurrency(concepto.importe_total)}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Progress 
                              value={concepto.importe_total > 0 ? (concepto.importe_pagado / concepto.importe_total * 100) : 0} 
                              className="h-1.5 w-24" 
                            />
                            {concepto.importe_pendiente <= 0 ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">
                                Pte: {formatCurrency(concepto.importe_pendiente)}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {expandedConceptos[concepto.id] ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="px-4 pb-4 border-t bg-gray-50">
                    {/* Acciones del concepto */}
                    <div className="flex justify-between items-center py-3">
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); handleOpenPagoDialog(concepto); }}
                          disabled={concepto.importe_pendiente <= 0}
                        >
                          <Banknote className="w-4 h-4 mr-2" />
                          Registrar Pago
                        </Button>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleOpenConceptoDialog(concepto); }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setSelectedConcepto(concepto);
                            setIsDeleteConceptoDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Lista de pagos */}
                    {concepto.pagos && concepto.pagos.length > 0 ? (
                      <div className="space-y-2">
                        <h5 className="text-sm font-medium text-gray-700">Pagos registrados</h5>
                        {concepto.pagos.map((pago) => (
                          <div key={pago.id} className="flex items-center justify-between bg-white rounded-lg p-3 border">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                <Check className="w-4 h-4 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-green-700">{formatCurrency(pago.importe)}</p>
                                <div className="flex gap-3 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDate(pago.fecha_pago)}
                                  </span>
                                  {pago.referencia && (
                                    <span className="flex items-center gap-1">
                                      <FileText className="w-3 h-3" />
                                      {pago.referencia}
                                    </span>
                                  )}
                                </div>
                                {pago.notas && (
                                  <p className="text-xs text-gray-400 mt-1">{pago.notas}</p>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setSelectedPago(pago);
                                setIsDeletePagoDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No hay pagos registrados para este concepto
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      {/* Dialog para Concepto */}
      <Dialog open={isConceptoDialogOpen} onOpenChange={setIsConceptoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingConcepto ? 'Editar Concepto de Pago' : 'Añadir Concepto de Pago'}
            </DialogTitle>
            <DialogDescription>
              {editingConcepto 
                ? 'Modifica el importe o descripción del concepto' 
                : 'Selecciona el tipo de concepto y especifica el importe a cobrar'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Concepto</Label>
              <Select 
                value={conceptoForm.tipo} 
                onValueChange={(v) => setConceptoForm({...conceptoForm, tipo: v})}
                disabled={!!editingConcepto}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un tipo" />
                </SelectTrigger>
                <SelectContent>
                  {(editingConcepto ? [{value: editingConcepto.tipo, label: editingConcepto.tipo_display}] : tiposDisponibles).map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>
                      <div className="flex items-center gap-2">
                        <span>{TIPOS_CONCEPTO[tipo.value]?.icon || '💰'}</span>
                        <span>{tipo.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Importe Total a Cobrar (€)</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className="pl-9"
                  value={conceptoForm.importe_total}
                  onChange={(e) => setConceptoForm({...conceptoForm, importe_total: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Textarea
                placeholder="Añade una descripción o notas adicionales..."
                value={conceptoForm.descripcion}
                onChange={(e) => setConceptoForm({...conceptoForm, descripcion: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConceptoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConcepto} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingConcepto ? 'Guardar Cambios' : 'Añadir Concepto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para Pago */}
      <Dialog open={isPagoDialogOpen} onOpenChange={setIsPagoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
            <DialogDescription>
              {selectedConcepto && (
                <>
                  Registrando pago para: <strong>{selectedConcepto.tipo_display}</strong>
                  <br />
                  Pendiente: <strong>{formatCurrency(selectedConcepto.importe_pendiente)}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Importe del Pago (€) *</Label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={selectedConcepto?.importe_pendiente}
                  placeholder="0.00"
                  className="pl-9"
                  value={pagoForm.importe}
                  onChange={(e) => setPagoForm({...pagoForm, importe: e.target.value})}
                />
              </div>
              {selectedConcepto && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setPagoForm({...pagoForm, importe: selectedConcepto.importe_pendiente.toString()})}
                >
                  Pagar todo: {formatCurrency(selectedConcepto.importe_pendiente)}
                </Button>
              )}
            </div>

            <div className="space-y-2">
              <Label>Fecha del Pago</Label>
              <Input
                type="date"
                value={pagoForm.fecha_pago}
                onChange={(e) => setPagoForm({...pagoForm, fecha_pago: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Referencia de Pago (opcional)</Label>
              <Input
                placeholder="Ej: Transferencia nº 123456"
                value={pagoForm.referencia}
                onChange={(e) => setPagoForm({...pagoForm, referencia: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                placeholder="Notas adicionales sobre el pago..."
                value={pagoForm.notas}
                onChange={(e) => setPagoForm({...pagoForm, notas: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPagoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSavePago} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Registrar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmación eliminar concepto */}
      <AlertDialog open={isDeleteConceptoDialogOpen} onOpenChange={setIsDeleteConceptoDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar concepto de pago?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el concepto <strong>{selectedConcepto?.tipo_display}</strong> y todos sus pagos asociados.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConcepto} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmación eliminar pago */}
      <AlertDialog open={isDeletePagoDialogOpen} onOpenChange={setIsDeletePagoDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pago?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el pago de <strong>{selectedPago && formatCurrency(selectedPago.importe)}</strong>.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePago} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PagosSection;
